import { Attribute } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { ListItem } from '@tiptap/extension-list-item';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { last, isEqual } from 'lodash';
import { Mark, Node as ProseMirrorNode } from 'prosemirror-model';
import { TextStyle } from './text-style';
import {
  CustomBulletListExtensionName,
  CustomListItemExtensionName,
  CustomOrderedListExtensionName,
} from './utils/names';

interface CustomListItemMarkerAttributes {
  bold?: boolean;
  italic?: boolean;
  fontSize?: string;
  fontFamily?: string;
  color?: string;
  shadow?: string;
}

export const CustomListItem = ListItem.extend({
  name: CustomListItemExtensionName,

  addAttributes(): Record<keyof CustomListItemMarkerAttributes, Partial<Attribute>> {
    return {
      bold: {
        renderHTML: (attributes) => {
          return {
            style: `--tiptap-listitem-marker-fontweight: ${attributes.bold ? '700' : 'initial'}`,
          };
        },
      },
      italic: {
        renderHTML: (attributes) => {
          return {
            style: `--tiptap-listitem-marker-fontstyle: ${attributes.italic ? 'italic' : 'initial'}`,
          };
        },
      },
      fontFamily: {
        renderHTML: (attributes) => {
          // We had to add " " around the font family name to support font families with numbers and special characters.
          // Without the quotes, we were unable to apply font families like Source Sans 3
          return {
            style: `--tiptap-listitem-marker-fontfamily: ${
              attributes.fontFamily ? `"${attributes.fontFamily}"` : 'initial'
            }`,
          };
        },
      },
      fontSize: {
        renderHTML: (attributes) => {
          return {
            style: `--tiptap-listitem-marker-fontsize: ${attributes.fontSize ? attributes.fontSize : 'initial'}`,
          };
        },
      },
      color: {
        renderHTML: (attributes) => {
          return {
            style: `--tiptap-listitem-marker-color: ${attributes.color ? attributes.color : 'initial'}`,
          };
        },
      },
      shadow: {
        renderHTML: (attributes) => {
          if (!attributes.shadow) {
            return {};
          }

          return {};
        },
      },
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(`handle${CustomListItemExtensionName}`),
        appendTransaction: (transactions, oldState, newState) => {
          let hasChanges = false;
          let tr = newState.tr;
          // If there's no selection range, there's no need to proceed
          if (!tr.selection.ranges.length) return null;

          const selectionRange = tr.selection.ranges[0];
          const selectionFromPosition = selectionRange.$from;
          const listItemNodes: { node: ProseMirrorNode; pos: number }[] = [];

          newState.doc.descendants((node, pos) => {
            if (node.type.name === CustomListItemExtensionName) {
              listItemNodes.push({ node, pos });
            }
          });

          // If the new state has no listItemNodes there's no need to proceed
          if (!listItemNodes.length) return null;

          const targetedListItem = last(listItemNodes.filter(({ pos }) => pos < selectionFromPosition.pos));

          // If the selection targets a list item we need to validate if it's one of the possible edge cases
          if (targetedListItem) {
            // In case of storedMarks, check if it's an empty listItemNode (to catch newline)
            if (tr.storedMarks && targetedListItem.node.content.firstChild?.content.size === 0) {
              const storedMarks = tr.storedMarks;
              tr = tr.setNodeMarkup(targetedListItem.pos, undefined, {
                ...getAttributesFromMarks(storedMarks),
              });
              /**
               * ProseMirror clears the storedMarks after a setNodeMarkup/setNodeAttribute transaction, so we need to set them again
               * @see https://discuss.prosemirror.net/t/does-tr-setnodeattribute-reset-stored-marks/6147
               */
              tr = tr.setStoredMarks(storedMarks);

              return tr;
            }

            // When you navigate to an empty list item, which causes a reset of textStyle, we need to reflect it on the marker
            if (
              selectionFromPosition.pos === selectionRange.$to.pos &&
              targetedListItem.node.content.firstChild?.content.size === 0
            ) {
              tr = tr.setNodeMarkup(targetedListItem.pos, undefined, {});
              hasChanges = true;
            }
          }

          listItemNodes.map(({ node, pos }) => {
            const attributes = processNodeAttributeChanges(node);
            if (attributes) {
              const storedMarks = tr.storedMarks;
              tr = tr.setNodeMarkup(pos, undefined, {
                ...attributes,
              });
              tr.setStoredMarks(storedMarks);
              hasChanges = true;
            }
          });

          return hasChanges ? tr : null;
        },
      }),
    ];
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      bulletListTypeName: CustomBulletListExtensionName,
      orderedListTypeName: CustomOrderedListExtensionName,
      types: ['textStyle'],
    };
  },
});

function processNodeAttributeChanges(node: ProseMirrorNode): CustomListItemMarkerAttributes | null {
  const firstContentChildMarks = node.firstChild?.content.firstChild?.marks;
  if (!firstContentChildMarks) return null;
  const newAttributes = getAttributesFromMarks(firstContentChildMarks);
  return isEqual(node.attrs, newAttributes) ? null : newAttributes;
}

function getAttributesFromMarks(marks: readonly Mark[]): CustomListItemMarkerAttributes {
  const markAttributes = marks.find((mark) => mark.type.name === TextStyle.name)?.attrs ?? {};
  const italicMark = marks.find((mark) => mark.type.name === Italic.name);
  const boldMark = marks.find((mark) => mark.type.name === Bold.name);

  const previousAttributes: CustomListItemMarkerAttributes = {
    bold: !!boldMark,
    italic: !!italicMark,
    fontSize: markAttributes['fontSize'] ?? null,
    fontFamily: markAttributes['fontFamily'] ?? null,
    color: markAttributes['color'] ?? null,
    shadow: markAttributes['shadow'] ?? null,
  };

  return previousAttributes;
}
